import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { removeNonNumericAndParse } from "../../hooks/utils";
import { Aircraft, FBO } from "../../types";
import { formatCurrency, parsePreciseNumber } from "../../utils";
import { AircraftSpecsTooltip } from "../../widgets/CurrentAircraft/AircraftSpecsTooltip";
import { MetricAwareTextField } from "../../widgets/MetricAwareTextField/MetricAwareTextField";
import { SexyText } from "../../widgets/SexyText/SexyText";
import { SelectTailNumber } from "../../widgets/SelectTailNumber/SelectTailNumber";
import { AutocompleteAircraft } from "../Settings/HangarSettings/AutocompleteAircraft";
import { AnnualTenantTable } from "../Settings/RevenueEstimator/AnnualTenantTable";
import { Rate } from "../Settings/RevenueEstimator/RatesForm";

export const FormLabel: React.FC<{
  title: string;
  subtitle: string;
}> = ({ title, subtitle }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography variant="subtitle2">{title}</Typography>
    <Tooltip title={subtitle}>
      <InfoIcon fontSize="small" sx={{ color: "info.dark" }} />
    </Tooltip>
  </Stack>
);

type Props = {
  aircraft: Aircraft;
  buffer: number;
  setBuffer: (buffer: number) => void;
  setAircaft: (aircraft: Aircraft) => void;
  aircraftSqft: string;
  aircraftSqftWithBuffer: string;
  baseMonthlyTenantDisplayValue: string;
  monthlyTenantValue: number;
  baseMonthlyTenantValue: number;
  monthlyTenantValueWithCAM: number;
  monthlyTenantDisplayValue: string;
  monthlyTenantDisplayValueWithCAM: string;
  baseDailyTenantDisplayValue: string;
  dailyTenantDisplayValue: string;
  dailyCamFees: number;
  monthlyCamFees: number;
  activeFBO: FBO;
  setFBO: (fbo: FBO) => void;
  nightlyRate: number;
  cam: number;
  setCam: (cam: number) => void;
  camPerSqft: number;
  setCamPerSqft: (cam: number) => void;
  dailyRate: number;
  setDailyRate: (rate: number) => void;
  monthlyRate: number;
  setMonthlyRate: (rate: number) => void;
  availableRates: Rate[];
};

export const FeeCalculator2Presenter: React.FC<Props> = ({
  aircraft,
  buffer,
  setBuffer,
  setAircaft,
  aircraftSqft,
  aircraftSqftWithBuffer,
  monthlyTenantValue,
  monthlyTenantValueWithCAM,
  monthlyTenantDisplayValue,
  monthlyTenantDisplayValueWithCAM,
  dailyTenantDisplayValue,
  monthlyCamFees,
  activeFBO,
  monthlyRate,
  nightlyRate,
  cam,
  camPerSqft,
  setCam,
  setCamPerSqft,
  dailyRate,
  setDailyRate,
  setMonthlyRate,
  availableRates,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = React.useState<string>(
    t('estimator.feeCalculator.tabs.tenantAndTransient')
  );
  const [selectedRate, setSelectedRate] = React.useState<string>(t('estimator.feeCalculator.rateLabels.default'));
  const unit = activeFBO?.settings?.useMetricSystem ? "m" : "ft";

  // if the user has picked a selectedRate and then modified one of those rates,
  // we want to reset the selectedRate to Default
  React.useEffect(() => {
    const activeRate = availableRates.find(
      (rate) => rate.label === selectedRate
    );
    const wereRatesModified =
      activeRate &&
      (activeRate.monthly_rate !== monthlyRate ||
        activeRate.nightly_rate !== nightlyRate);
    const defaultLabel = t('estimator.feeCalculator.rateLabels.default');
    if (activeRate?.label !== defaultLabel && wereRatesModified) {
      setSelectedRate(defaultLabel);
    }
  }, [monthlyRate, nightlyRate, t]);

  return (
    <Stack
      direction="row"
      justifyContent="space-around"
      alignItems="flex-start"
      maxWidth="100%"
    >
      <Stack direction="column" spacing={1} p={2}>
        <Typography>{t('estimator.feeCalculator.sections.inputs')}</Typography>
        <Divider />
        <Box width={600}>
          <Stack direction="column" spacing={1}>
            <Stack direction="column" spacing={1}>
              <FormLabel
                title={t('estimator.feeCalculator.inputs.aircraft.title')}
                subtitle={t('estimator.feeCalculator.inputs.aircraft.subtitle')}
              />
              <Stack
                direction="row"
                spacing={1}
                width="100%"
                alignItems="flex-start"
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box width={150}>
                    <SelectTailNumber
                      disableAutocomplete={false}
                      onChange={(tenant) => {
                        if (tenant?.aircraft) {
                          setAircaft(tenant.aircraft);
                        }
                      }}
                    />
                  </Box>
                  <Box width={275}>
                    <AutocompleteAircraft
                      label={t('estimator.feeCalculator.inputs.aircraft.makeModel')}
                      onSelect={setAircaft}
                      defaultSelected={aircraft}
                    />
                  </Box>
                </Stack>
                <Stack direction="column" alignItems="center">
                  <Typography variant="subtitle2">{t('estimator.feeCalculator.inputs.aircraft.dimensions')}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">{aircraftSqft}</Typography>
                    {aircraft ? (
                      <AircraftSpecsTooltip aircraft={aircraft} />
                    ) : null}
                  </Stack>
                </Stack>
              </Stack>
              <FormLabel
                title={t('estimator.feeCalculator.inputs.buffer.title')}
                subtitle={t('estimator.feeCalculator.inputs.buffer.subtitle', { unit })}
              />
              <MetricAwareTextField
                type="number"
                unit={activeFBO?.settings?.useMetricSystem ? "meters" : "feet"}
                inputProps={{
                  min: 0.0,
                  max: 5,
                  step: activeFBO?.settings?.useMetricSystem ? 0.1 : 0.5,
                  inputMode: "decimal",
                }}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">{unit}</InputAdornment>
                  ),
                }}
                onChange={(value) => setBuffer(Number(value))}
                value={buffer}
                sx={{ width: 125 }}
              />
            </Stack>
            <Stack direction="column" spacing={1}>
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <Stack direction="column" spacing={1}>
                  <FormLabel
                    title={t('estimator.feeCalculator.inputs.baseTenantRate.title')}
                    subtitle={t('estimator.feeCalculator.inputs.baseTenantRate.subtitle')}
                  />
                  <TextField
                    label={t('estimator.feeCalculator.inputs.baseTenantRate.label', { unit })}
                    variant="outlined"
                    type="number"
                    size="small"
                    inputProps={{
                      step: 0.001,
                      min: 0.01,
                      max: 100,
                      inputMode: "decimal",
                    }}
                    sx={{ width: 200 }}
                    onChange={(evt) =>
                      setMonthlyRate(parsePreciseNumber(evt.target.value))
                    }
                    value={monthlyRate}
                  />
                </Stack>
                <Stack direction="column" spacing={1}>
                  <FormLabel
                    title={t('estimator.feeCalculator.inputs.transientRate.title')}
                    subtitle={t('estimator.feeCalculator.inputs.transientRate.subtitle')}
                  />
                  <TextField
                    label={t('estimator.feeCalculator.inputs.transientRate.label', { unit })}
                    variant="outlined"
                    type="number"
                    size="small"
                    inputProps={{
                      step: 0.001,
                      min: 0.01,
                      max: 100,
                      inputMode: "decimal",
                    }}
                    sx={{ width: 200 }}
                    onChange={(evt) =>
                      setDailyRate(parsePreciseNumber(evt.target.value))
                    }
                    value={dailyRate}
                  />
                </Stack>

                <Stack direction="column" spacing={1}>
                  <FormLabel
                    title={t('estimator.feeCalculator.inputs.presets.title')}
                    subtitle={t('estimator.feeCalculator.inputs.presets.subtitle')}
                  />
                  <TextField
                    select
                    label={t('estimator.feeCalculator.inputs.presets.label')}
                    size="small"
                    sx={{ width: 225 }}
                    onChange={(evt) => {
                      const label = evt.target.value;
                      const rate = availableRates.find(
                        (rate) => rate.label === label
                      );
                      setMonthlyRate(rate.monthly_rate);
                      setDailyRate(rate.nightly_rate);
                      setSelectedRate(label);
                    }}
                    value={selectedRate}
                  >
                    {availableRates.map((rate) => (
                      <MenuItem key={rate.id} value={rate.label}>
                        {rate.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Stack>
              <FormLabel
                title={t('estimator.feeCalculator.inputs.camFees.title')}
                subtitle={t('estimator.feeCalculator.inputs.camFees.subtitle', { unit })}
              />
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  type="number"
                  inputProps={{
                    min: 0.0,
                    max: 100,
                    step: 1,
                    inputMode: "numeric",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  size="small"
                  onChange={(evt) => {
                    // Parse the value without the percentage sign
                    const value = parsePreciseNumber(evt.target.value);
                    setCam(value || 0);
                    setCamPerSqft(0);
                  }}
                  value={cam}
                  sx={{ width: 120 }}
                />
                <Typography>{t('estimator.feeCalculator.inputs.camFees.or')}</Typography>
                <MetricAwareTextField
                  unit={
                    activeFBO?.settings?.useMetricSystem ? "meters" : "feet"
                  }
                  decimals={2}
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">/ {unit}²</InputAdornment>
                    ),
                  }}
                  size="small"
                  onChange={(value) => {
                    setCamPerSqft(value || 0);
                    setCam(0);
                  }}
                  value={camPerSqft}
                  sx={{ width: 140 }}
                />
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Stack>
      <Divider orientation="vertical" flexItem />
      <Box width={600}>
        <Stack direction="column" spacing={1} p={2}>
          <Typography>{t('estimator.feeCalculator.sections.calculations')}</Typography>
          <Divider />
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
          >
            <Tab
              value={t('estimator.feeCalculator.tabs.tenantAndTransient')}
              label={t('estimator.feeCalculator.tabs.tenantAndTransient')}
            />
            <Tab value={t('estimator.feeCalculator.tabs.annualFees')} label={t('estimator.feeCalculator.tabs.annualFees')} />
          </Tabs>
          {activeTab === t('estimator.feeCalculator.tabs.tenantAndTransient') && (
            <Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <FormLabel
                        title={t('estimator.feeCalculator.calculations.aircraftSquare', { unit: unit === "m" ? t('common.units.meters') : t('common.units.feet') })}
                        subtitle={t('estimator.feeCalculator.calculations.aircraftSquareSubtitle')}
                      />
                    </TableCell>
                    <TableCell>
                      <FormLabel
                        title={t('estimator.feeCalculator.calculations.totalSquare', { unit: unit === "m" ? t('common.units.meters') : t('common.units.feet') })}
                        subtitle={t('estimator.feeCalculator.calculations.totalSquareSubtitle')}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{aircraftSqft}</TableCell>
                    <TableCell>
                      <SexyText
                        bidirectional
                        text={aircraftSqftWithBuffer}
                        value={removeNonNumericAndParse(aircraftSqftWithBuffer)}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <br />
              <br />
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>{t('estimator.feeCalculator.calculations.aircraftFee')}</TableCell>
                    <TableCell>{t('estimator.feeCalculator.calculations.camFees')}</TableCell>
                    <TableCell>
                      <Typography
                        variant="inherit"
                        align="center"
                        sx={{
                          borderBottomColor: "success.main",
                          borderBottomWidth: 2,
                          borderBottomStyle: "solid",
                        }}
                      >
                        {t('estimator.feeCalculator.calculations.totalFee')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{t('estimator.feeCalculator.calculations.tenantMonthly')}</TableCell>
                    <TableCell>{monthlyTenantDisplayValue}</TableCell>
                    <TableCell>
                      {monthlyCamFees
                        ? formatCurrency(monthlyCamFees, 2)
                        : "---"}
                    </TableCell>
                    <TableCell>
                      <SexyText
                        bidirectional
                        value={removeNonNumericAndParse(
                          monthlyTenantDisplayValueWithCAM
                        )}
                        text={monthlyTenantDisplayValueWithCAM}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('estimator.feeCalculator.calculations.transientDaily')}</TableCell>
                    <TableCell>{dailyTenantDisplayValue}</TableCell>
                    <TableCell>{"---"}</TableCell>
                    <TableCell>
                      <SexyText
                        bidirectional
                        value={removeNonNumericAndParse(
                          dailyTenantDisplayValue
                        )}
                        text={dailyTenantDisplayValue}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
          {activeTab === t('estimator.feeCalculator.tabs.annualFees') && (
            <AnnualTenantTable
              aircraftFee={monthlyTenantValue}
              camFees={monthlyCamFees}
              totalFee={monthlyTenantValueWithCAM}
            />
          )}
        </Stack>
      </Box>
    </Stack>
  );
};
